import { z } from 'zod';
import { Company } from '@upright/common/model/input/companies/types';
import { SsoProvider } from '../../login/types';
import { Organization } from '../../organizations/organization-types';
import { UserGroupInvitation, UserGroupPlain, UserGroupType } from '../../user-groups/types';
import { FriendlyUserDisplayName, UserPlain } from '../../users/types';

export type GetUserGroupResponse = UserGroupPlain;
export type GetUserGroupReferenceResponse = Pick<UserGroupPlain, 'id' | 'name'>;

export interface CreateUserGroupRequest {
  name: string;
  description?: string;
  parentGroupId?: number;
  type?: UserGroupType;
}

export type CreateUserGroupResponse = UserGroupPlain;

export interface AddMembersToUserGroupRequest {
  groupId: number;
  memberUserIds?: number[];
  childGroupIds?: number[];
}

export interface ChangeMembersUserGroupRequest {
  newGroupId: number;
}

export const inviteNewUserRequestSchema = z.object({
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  welcomeMessage: z.string().max(300, 'Please write a max 300 characters long message').optional(),
  role: z.enum(['member', 'admin']).optional(),
  companyIds: z.array(z.string()).optional(),
  // A member policy of the organisation, behind USE_SELF_SERVE_MEMBERS; none means the baseline only.
  policyId: z.string().uuid().nullable().optional(),
});

export type InviteNewUserRequest = z.infer<typeof inviteNewUserRequestSchema>;

/**
 * Discriminated response for the invite endpoint.
 *
 * - `outcome: 'invited'` — a new invitation was created; `invitation` is the
 *   persisted `UserGroupInvitation` row.
 * - `outcome: 'added'` — the invitee was already an organization member but
 *   did not yet have access to the target group; they were added directly
 *   with no invitation row created. The server returns only the identifying
 *   fields (no synthetic id/timestamps) so UI callers cannot accidentally
 *   treat it as a real invitation.
 * - `outcome: 'already_member'` — the invitee already has the exact access
 *   being requested (direct member of the target group, and admin of it if
 *   `role: 'admin'` was requested). Nothing was written to the database and
 *   no notification email was sent; the UI should tell the inviter the user
 *   already has access rather than showing a generic "invitation sent" or
 *   "added directly" confirmation.
 */
export type InviteUserToUserGroupResponse =
  | {
      outcome: 'invited';
      invitation: UserGroupInvitation;
    }
  | {
      outcome: 'added';
      userGroupId: number;
      userEmail: string;
    }
  | {
      outcome: 'already_member';
      userGroupId: number;
      userEmail: string;
    };

export interface RemoveUserInvitationRequest {
  invitationId: string;
}

export type RemoveUserInvitationResponse = { success: true };
export type ListUserGroupInvitationsResponse = {
  invitations: UserGroupInvitation[];
};
export type AddMembersToUserGroupResponse = unknown; // We don't care about the return value

export interface GetCompanyUserGroupsResponse {
  memberGroupId: number | null;
  adminGroupId: number | null;
  /** User IDs of members belonging to org-level user groups (both member and admin).
   * Used by frontend to exclude these users from company-specific seat count.
   * Only populated for DMA Consultant company-level user groups (Strategy 1). */
  orgLevelUserIds?: number[];
  /** The current user's role at the company level (for DMA consultant company-level access control) */
  currentUserCompanyRole?: 'ADMIN' | 'MEMBER';
  /**
   * The parent (consultant) organization ID for this company, if any.
   *
   * PLT-4360: This lets the frontend call `/organizations/:orgId/...` endpoints
   * (e.g. the "Invite parent members" flow) with the *customer's* consultant
   * org rather than the logged-in user's own org. The distinction matters for
   * Upright team users, whose `getOrganizationOfUser` returns the Upright team
   * org — not the customer consultant org — so the previous `org?.id`-based
   * approach silently failed auth and the button never worked for team users.
   */
  parentOrganizationId?: number | null;
  /**
   * Whether the current user is a direct member (or admin) of the parent
   * organization's user group.
   *
   * PLT-4360: The "Invite parent members" action grants access to *existing*
   * members of the parent (consultant) org. It only makes sense for users who
   * are themselves members of that org — an assessment-only user whose only
   * access is to this one company is not a parent-org member even though their
   * primary organization walk reaches the consultant org via the shared
   * group hierarchy, and showing the button to them produces an empty list
   * plus a confusing "this isn't yours to share" action. Team users (Upright
   * staff) are always treated as if they're a parent-org member for this UI
   * purpose because they manage every customer org.
   *
   * Only populated for company-level groups that have a resolvable parent org
   * (i.e. DMA Consultant company groups).
   */
  currentUserIsParentOrgMember?: boolean;
}
export type RemoveMembersFromUserGroupRequest = AddMembersToUserGroupRequest;
export type RemoveMembersFromUserGroupResponse = unknown; // We don't care about the return value

export type GetInvitationResponse = UserGroupInvitation & {
  isExpired: boolean;
  organization: Pick<Organization, 'id' | 'name' | 'type' | 'description' | 'companyId'> & {
    company?: Pick<Company, 'id' | 'name' | 'websiteURL'>; // Optional for DMA Consultant orgs
  };
  // For DMA Consultant orgs, the user group itself has a company (the client company)
  // whereas the organization's company is null
  userGroupCompany?: Pick<Company, 'id' | 'name' | 'websiteURL'>;
  // Populated for org-level invitations (to organization_member group) for DMA Consultant orgs
  organizationName?: string;
  createdByUser: FriendlyUserDisplayName | null;
};

export interface GetOrganizationUserGroupsResponse {
  memberGroupId: number;
  adminGroupId: number;
  organizationName: string;
}

export interface GetInheritedMembersResponse {
  members: Array<{
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
    accessSource: 'organization';
    effectiveRole: 'admin';
  }>;
}

export interface GetMemberAssessmentsResponse {
  memberAssessments: Array<{
    userId: number;
    isOrgAdmin: boolean;
    companies: Array<{
      companyId: string;
      companyName: string;
    }>;
  }>;
  /** Users who have been invited to specific assessments (company-level groups) but are not org-level members. */
  assessmentOnlyUsers: Array<{
    userId: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
    companies: Array<{
      companyId: string;
      companyName: string;
      userGroupId: number;
    }>;
  }>;
}

export type GetUserGroupMembersResponse = {
  members: UserPlain[];
};

export type OrgMemberAccessStatus =
  | 'none'
  | 'direct_member'
  | 'direct_admin'
  | 'inherited_admin'
  | 'invitation_pending';

export interface GetOrgMembersForCompanyResponse {
  members: Array<{
    userId: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    accessStatus: OrgMemberAccessStatus;
  }>;
}

export interface AddOrgMembersToCompanyRequest {
  userIds: number[];
  role: 'member' | 'admin';
}

export interface AddOrgMembersToCompanyResponse {
  addedCount: number;
}

export type UserAccessStatus = 'none' | 'direct_member' | 'direct_admin' | 'inherited_admin';

export interface GetUserAccessResponse {
  assessments: Array<{
    companyId: string;
    companyName: string;
    accessStatus: UserAccessStatus;
  }>;
}

export interface UpdateUserAccessRequest {
  companyIds: string[];
}

/**
 * PLT-4360: "All assessments under an organization" response — a thin list of
 * every company-level assessment owned by the organization, without any
 * user-specific access status. Used by the Invite Colleagues dialog so the
 * inviter can grant access to assessments that have no existing members yet.
 */
export interface GetOrgAssessmentsResponse {
  assessments: Array<{
    companyId: string;
    companyName: string;
  }>;
}

export interface UpdateUserAccessResponse {
  added: number;
  removed: number;
}

export interface EditUserGroupNameRequest {
  groupId: number;
  newName: string;
}

export type EditUserGroupNameResponse = unknown; // We don't care about the return value

export interface EditUserGroupDescriptionRequest {
  groupId: number;
  newDescription: string | undefined;
}

export type EditUserGroupDescriptionResponse = unknown; // We don't care about the return value

export type GetUserGroupMetadataForUserCreationRequest = {
  groupIds: number[];
};
export type GetUserGroupMetadataForUserCreationResponse = {
  dataByGroupId: {
    [groupId: string]: {
      linkedOrganization: { id: number; name: string } | undefined;
      ssoProviders: SsoProvider[];
    };
  };
  error?: string;
};

// FIXME: These should be group properties instead of naming convention
export const ADMIN_GROUP_SUFFIX = ' [ADMINS]';
